import React, { useState } from "react";
import { Tag, Tooltip, Dropdown } from "antd";
import { SettledBadge, settledStyle } from "helpers/useSettledRows";
import {
  EllipsisOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { T, STATUS_COLORS, STATUS_CHIPS, PRIORITY_CHIPS } from "../theme";
import { dueChipLabel, formatDue, isPastDue, daysToDue } from "helpers/issueDueDate";
import { issueHref } from "helpers/issueHref";

const STATUS_STYLE = {
  open: { dot: STATUS_COLORS.open, bg: STATUS_CHIPS.open.bg, fg: STATUS_CHIPS.open.fg, label: "Open", icon: <ClockCircleOutlined /> },
  pending: { dot: STATUS_COLORS.pending, bg: STATUS_CHIPS.pending.bg, fg: STATUS_CHIPS.pending.fg, label: "Pending", icon: <ExclamationCircleOutlined /> },
  resolved: { dot: STATUS_COLORS.resolved, bg: STATUS_CHIPS.resolved.bg, fg: STATUS_CHIPS.resolved.fg, label: "Resolved", icon: <CheckCircleOutlined /> },
  abandoned: { dot: STATUS_COLORS.abandoned, bg: STATUS_CHIPS.abandoned.bg, fg: STATUS_CHIPS.abandoned.fg, label: "Abandoned", icon: <StopOutlined /> },
};

const PRIORITY_STYLE = {
  urgent: { bg: PRIORITY_CHIPS.urgent.bg, fg: PRIORITY_CHIPS.urgent.fg, label: "Urgent" },
  normal: { bg: PRIORITY_CHIPS.normal.bg, fg: PRIORITY_CHIPS.normal.fg, label: "Normal" },
  low: { bg: PRIORITY_CHIPS.low.bg, fg: PRIORITY_CHIPS.low.fg, label: "Low" },
};

const SOURCE_LABEL = {
  docControl: { label: "2D", bg: T.wash, fg: T.navyD },
  modelViewer: { label: "3D", bg: "#F1E8FB", fg: "#7A4BBF" },
  blank: { label: "—", bg: "#F3F4F5", fg: "#526470" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.open;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.fg,
      padding: "2px 7px", borderRadius: 999,
      fontSize: 10.5, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {s.label}
    </span>
  );
}

function PriPill({ priority }) {
  const p = PRIORITY_STYLE[priority] || PRIORITY_STYLE.normal;
  return (
    <span style={{
      background: p.bg, color: p.fg, padding: "2px 6px", borderRadius: 3,
      fontSize: 9.5, fontWeight: 600, letterSpacing: ".04em",
    }}>{p.label.toUpperCase()}</span>
  );
}

// Due-date chip — same reading as on the board: red once the due day has
// passed, amber on the last day, and nothing at all when no date is set.
function DuePill({ dueDate, status }) {
  if (!dueDate) return null;
  const settled = ["resolved", "abandoned"].includes(status);
  const late = !settled && isPastDue(dueDate);
  const soon = !settled && !late && daysToDue(dueDate) <= 1;
  // Once the work is off the table the date is history, not a deadline:
  // a resolved issue reads "SEP 14", never "10D LATE".
  const label = settled ? formatDue(dueDate, "MMM D") : dueChipLabel(dueDate);
  const c = late
    ? { bg: T.removedBg, fg: T.removed2 }
    : soon
      ? { bg: T.ambBg, fg: "#8A6112" }
      : { bg: "#F3F4F5", fg: "#526470" };
  return (
    <Tooltip title={`Due ${formatDue(dueDate, "MMMM D, YYYY")}`}>
      <span style={{
        background: c.bg, color: c.fg, padding: "2px 6px", borderRadius: 3,
        fontSize: 9.5, fontWeight: 600, letterSpacing: ".04em", whiteSpace: "nowrap",
      }}>{label.toUpperCase()}</span>
    </Tooltip>
  );
}

function SourceTag({ sourceType }) {
  const s = SOURCE_LABEL[sourceType] || SOURCE_LABEL.blank;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: "2px 5px", borderRadius: 3,
      fontSize: 9.5, fontWeight: 700, letterSpacing: ".04em",
    }}>{s.label}</span>
  );
}

function groupByProject(issues) {
  const groups = new Map();
  (issues || []).forEach(m => {
    const key = m.projectId?._id || m.projectId || "__no_project__";
    const title = m.projectId?.title || "No project";
    if (!groups.has(key)) {
      groups.set(key, { key, title, items: [] });
    }
    groups.get(key).items.push(m);
  });
  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === "__no_project__") return 1;
    if (b.key === "__no_project__") return -1;
    return a.title.localeCompare(b.title);
  });
}

export default function IssueByProjectView({ issues, onOpenViewer, getActionMenuItems, apiLoading }) {
  const groups = groupByProject(issues);
  const [collapsed, setCollapsed] = useState({});

  const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  if (!apiLoading && groups.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 24px", color: T.muted }}>
        <p>No issues found</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
      {groups.map(g => {
        const isCollapsed = !!collapsed[g.key];
        const open = g.items.filter(i => (i.status || "open") === "open").length;
        const pending = g.items.filter(i => i.status === "pending").length;
        const resolved = g.items.filter(i => i.status === "resolved").length;
        const abandoned = g.items.filter(i => i.status === "abandoned").length;
        const urgent = g.items.filter(i => i.priority === "urgent").length;
        const overdue = g.items.filter(
          i => !["resolved", "abandoned"].includes(i.status) && isPastDue(i.dueDate)
        ).length;

        return (
          <div key={g.key} style={{
            background: "#fff",
            border: `1px solid ${T.line}`,
            borderRadius: 8,
            overflow: "hidden",
          }}>
            <div
              onClick={() => toggle(g.key)}
              style={{
                padding: "10px 14px",
                borderBottom: isCollapsed ? "none" : `1px solid ${T.line}`,
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: T.bg,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {isCollapsed ? <CaretRightOutlined style={{ color: T.muted }} /> : <CaretDownOutlined style={{ color: T.muted }} />}
              <span style={{
                background: T.navyD, color: "#fff", padding: "3px 8px", borderRadius: 3,
                fontSize: 11, fontWeight: 700, letterSpacing: ".02em",
              }}>{g.title}</span>
              <span style={{ fontSize: 12, color: T.muted }}>
                {g.items.length} issue{g.items.length === 1 ? "" : "s"}
              </span>
              <div style={{ flex: 1 }} />
              {urgent > 0 && (
                <span style={{
                  background: T.removedBg, color: T.removed2,
                  padding: "2px 8px", borderRadius: 999,
                  fontSize: 10.5, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                  <WarningOutlined style={{ fontSize: 10 }} /> {urgent} urgent
                </span>
              )}
              {open > 0 && <StatusCount dotColor={STATUS_COLORS.open} label={`${open} open`} />}
              {pending > 0 && <StatusCount dotColor={STATUS_COLORS.pending} label={`${pending} pending`} />}
              {resolved > 0 && <StatusCount dotColor={STATUS_COLORS.resolved} label={`${resolved} resolved`} />}
              {abandoned > 0 && <StatusCount dotColor={STATUS_COLORS.abandoned} label={`${abandoned} abandoned`} />}
              {overdue > 0 && <StatusCount dotColor={T.removed2} label={`${overdue} overdue`} />}
            </div>

            {!isCollapsed && (
              <div>
                {g.items.map(item => (
                  <div
                    key={item._id}
                    onClick={() => onOpenViewer(item)}
                    style={{
                      ...settledStyle(item),
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      borderBottom: `1px dashed ${T.line}`,
                      cursor: "pointer",
                      transition: "background .15s",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = T.bg)}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <SourceTag sourceType={item.sourceType} />
                    <span style={{
                      fontSize: 13, fontWeight: 500, color: T.ink,
                      flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      <a href={issueHref(item)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "inherit" }}>
                        {item.title || "Untitled"}
                      </a>
                      <SettledBadge row={item} />
                    </span>
                    <StatusPill status={item.status} />
                    <PriPill priority={item.priority} />
                    <span style={{ minWidth: 78, textAlign: "right" }}>
                      <DuePill dueDate={item.dueDate} status={item.status} />
                    </span>
                    <span style={{ fontSize: 11, color: T.muted, minWidth: 90, textAlign: "right" }}>
                      {item.createdBy?.name?.split(" ")[0] || "—"}
                    </span>
                    <Tooltip title={moment(item.updatedAt).format("MMMM D, YYYY h:mm A")}>
                      <span style={{ fontSize: 11, color: T.sub, minWidth: 80, textAlign: "right" }}>
                        {moment(item.updatedAt).fromNow()}
                      </span>
                    </Tooltip>
                    <span style={{ fontSize: 11, color: T.muted, minWidth: 40, textAlign: "right" }}>
                      ◆ {item.issueObjectsCount || 0}
                    </span>
                    <Dropdown menu={{ items: getActionMenuItems(item) }} trigger={["click"]}>
                      <button
                        onClick={e => e.stopPropagation()}
                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2 }}
                      >
                        <EllipsisOutlined style={{ fontSize: 16, color: T.muted }} />
                      </button>
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusCount({ dotColor, label }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 10.5, color: T.muted, fontWeight: 500,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor }} />
      {label}
    </span>
  );
}
